import React from 'react';
import './CaseStudies.css';

const CaseStudies = () => {
    const cases = [
        {
            id: 4,
            title: "NLP-Based Automated Dialer",
            subtitle: "Intelligent, cost-effective outbound calling — zero LLM overhead",
            techStack: ["Twilio SIP Trunking", "FreeSwitch", "Rasa NLU", "OpenAI Whisper", "Python"],
            overview: "A high-performance, deterministic outbound dialer engineered for large-scale campaigns such as appointment reminders, payment notifications, and surveys — delivering reliable automation at a fraction of the cost of LLM-based alternatives.",
            challenge: "LLM-powered dialers incur high per-call API expenses, unpredictable latency, and serious risks of hallucinations. With thousands of daily calls, inference costs become prohibitive while inconsistent AI behavior jeopardizes compliance.",
            solution: "Designed a deterministic NLP architecture using Rasa for intent classification and Whisper for STT. Pre-recorded responses trigger instantly based on detected intents, guaranteeing 100% script adherence. Asterisk + Twilio provides scalable telephony, while custom Python logic manages call flows.",
            impact: [
                "70% cost reduction vs. LLM-based dialers",
                "Zero hallucination incidents across 50,000+ calls",
                "Response time < 200 ms",
                "100% script compliance"
            ],
            image: "/atoraxion-dialer.png",
            restricted: true,
        },
        {
            id: 5,
            title: "RAG-Based Chat Agent",
            subtitle: "Always-on, always-accurate business knowledge",
            techStack: ["n8n", "Supabase", "Google Gemini", "Anthropic Claude"],
            overview: "A powerful, fully contextual conversational agent that acts as an instant, 24/7 expert on any business — powered by Retrieval-Augmented Generation (RAG) and a comprehensive vectorized knowledge base.",
            challenge: "Business owners lose revenue from missed inquiries. Built-in auto-responders are generic, and rule-based chatbots fail at nuances. Manual follow-up is time-consuming and unscalable.",
            solution: "Created a RAG pipeline that vectorizes business assets (FAQs, pricing, policies) into Supabase. n8n orchestrates queries to Gemini/Claude for precise, grounded answers. The agent maintains context, handles unknowns gracefully, and integrates with messaging channels in <48 hours.",
            impact: [
                "Response time reduced from hours to seconds",
                "40% fewer repetitive inquiries for owners",
                "95% factual accuracy",
                "Virtually eliminates hallucinations"
            ],
            image: "/atoraxion-rag.png",
            restricted: true,
        },
        {
            id: 6,
            title: "Custom POS System",
            subtitle: "Modular platform that cuts operational overhead by 60%",
            techStack: ["React.js", "Node.js", "PostgreSQL", "Redis", "WebSockets"],
            overview: "A configurable, modular POS delivering fully tailored workflow automation for retail and food service businesses in weeks, at 60–70% lower cost than traditional custom development.",
            challenge: "Off-the-shelf POS systems force workflow changes, while custom builds are prohibitively expensive and slow. Businesses struggle with manual tracking, stockouts, and a lack of actionable insights.",
            solution: "Engineered a battle-tested core platform with pluggable modules. Features real-time inventory sync via WebSockets, offline-first capability, and custom business logic. Cloud-hosted for scalability and tailored without reinventing core features.",
            impact: [
                "Inventory management time reduced from 8 to 2 hours/week",
                "Stockouts reduced by 80% (12% revenue recovered)",
                "Unified multi-site view for chains",
                "ROI achieved in 4 months"
            ],
            image: "/atoraxion-pos.png",
            restricted: true,
        },
        {
            id: 1,
            title: "LEAD GENERATION WORKFLOW",
            subtitle: "Automated Google Maps Intelligence",
            techStack: ["n8n", "Google Maps API", "Google Sheets", "Node.js"],
            overview: "An intelligent automation workflow that systematically harvests and qualifies business leads from Google Maps, integrating directly with CRM pipelines.",
            challenge: "Manual lead sourcing from Google Maps is labor-intensive, error-prone, and difficult to scale. Businesses struggle to maintain up-to-date contact lists without investing significant man-hours.",
            solution: "Developed a custom n8n workflow that orchestrates the extraction process. It queries the Maps API, filters results based on strict criteria (e.g., valid email presence), checks for duplicates against existing databases, and formats the data for immediate sales outreach.",
            impact: [
                "90% reduction in time spent on lead sourcing",
                "Zero duplicates with automated filtering",
                "Real-time population of sales pipelines"
            ],
            image: "/553429057_1170363528244722_77337338452954765_n.png",
            downloadFile: "/new-leads-workflow.json",
            downloadName: "new-leads-workflow.json"
        },
        {
            id: 2,
            title: "VID-GENERATOR",
            subtitle: "Autonomous Content Production",
            techStack: ["n8n", "Node.js", "FFmpeg", "OpenAI API"],
            overview: "A seamless pipeline for generating video content automatically. It handles everything from script generation to asset compilation and rendering.",
            challenge: "Creating volume video content for social media or marketing is a bottleneck. Consistency and speed are often compromised when relying on manual editing workflows.",
            solution: "Built an end-to-end automation system. The workflow consumes raw topics, generates scripts using LLMs, sources relevant media assets via APIs, and stitches them together using programmatic video processing tools.",
            impact: [
                "Fully automated production cycle",
                "Consistent brand styling across all outputs",
                "Capable of scaling to hundreds of videos per day"
            ],
            image: "/Screenshot 2025-09-26 233453.png",
            downloadFile: "/vid-generator-workflow.json",
            downloadName: "vid-generator-n8n-workflow.json"
        },
        {
            id: 3,
            title: "PEERLY",
            subtitle: "Real-Time P2P Language Platform",
            techStack: ["React", "Socket.IO", "Web Speech API", "Node.js"],
            overview: "An interactive web application connecting english learners for real-time conversation practice, enhanced by AI-driven pronunciation feedback.",
            challenge: "Traditional language learning apps often lack spontaneous, real-time interaction. Students need a safe , engaging environment to practice speaking without fear of judgment.",
            solution: "Architected a peer-to-peer platform using WebRTC principles. Features include character-based roleplay scenarios (e.g., Family Guy dialogs) to gamify the experience and real-time speech analysis to provide instant corrective feedback.",
            impact: [
                "Low-latency real-time voice communication",
                "Gamified engagement increases session duration",
                "Instant AI feedback accelerates pronunciation improvement"
            ],
            image: "/Screenshot 2025-09-27 012419.png",
            github: "https://github.com/grid-is-afk/peerly.git"
        }
    ];

    const handleAction = (item) => {
        if (item.restricted) return;
        if (item.downloadFile) {
            const link = document.createElement('a');
            link.href = item.downloadFile;
            link.download = item.downloadName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else if (item.github) {
            window.open(item.github, '_blank');
        }
    };

    return (
        <section id="case-studies" className="case-studies-section">
            <div className="container">
                <h2 className="section-title">Case Studies</h2>
                <div className="case-studies-list">
                    {cases.map((project) => (
                        <article key={project.id} className="case-study-item">
                            <div className="case-header">
                                <h3 className="case-title">{project.title}</h3>
                                <p className="case-subtitle">{project.subtitle}</p>
                                <div className="tech-stack">
                                    {project.techStack.map((tech, i) => (
                                        <span key={i} className="tech-chip">{tech}</span>
                                    ))}
                                </div>
                            </div>

                            <div className="case-content">
                                <div className="case-details">
                                    <div className="detail-block">
                                        <h4>Overview</h4>
                                        <p>{project.overview}</p>
                                    </div>
                                    <div className="detail-block">
                                        <h4>The Challenge</h4>
                                        <p>{project.challenge}</p>
                                    </div>
                                    <div className="detail-block">
                                        <h4>Our Solution</h4>
                                        <p>{project.solution}</p>
                                    </div>
                                    <div className="detail-block">
                                        <h4>Key Impact</h4>
                                        <ul className="impact-list">
                                            {project.impact.map((point, i) => (
                                                <li key={i}>{point}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    {project.restricted ? (
                                        <div className="restricted-message">
                                            <span className="lock-icon">🔒</span>
                                            Access to codebase restricted for confidentiality purposes
                                        </div>
                                    ) : (
                                        <button className="action-btn" onClick={() => handleAction(project)}>
                                            {project.downloadFile ? 'Download Workflow' : 'View Codebase'}
                                        </button>
                                    )}
                                </div>

                                <div className="case-visual">
                                    <div className="image-container">
                                        <img src={project.image} alt={project.title} />
                                        <div className="overlay"></div>
                                    </div>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default CaseStudies;
